import { resolvePdfImage } from "./pdf-image.js";
import type { TDocumentDefinitions } from "pdfmake/interfaces.js";
import { contextStore } from "../../presentation/auth/context-store.js";
import { ErrorCode, AppError } from "../../shared/errors/app-error.js";
import { resolveAppVersion, getRuntimeBuildInfo } from "../../shared/branding/app-version.js";

declare const __APP_VERSION__: string;
declare const __APP_BUILD_TIME__: string;
declare const __APP_BACKEND_ENV__: string;

export const DOCUMENT_ENGINE_RUNTIME_ID = "document-engine-r6-central";

export function assertPdfMetadataReady(docDefinition: TDocumentDefinitions): void {
  if (!docDefinition) {
    throw new AppError(ErrorCode.Validation, "Definição do documento ausente.");
  }
  const info = docDefinition.info as any;
  if (!info) return;

  // Cria um objeto plano novo, livre de herança de protótipo e de chaves maiúsculas
  const cleanInfo: Record<string, any> = {};
  for (const key of Object.keys(info)) {
    if (key === "CreationDate" || key === "ModDate") continue;
    cleanInfo[key] = info[key];
  }

  if (info["title"]) cleanInfo["title"] = String(info["title"]);
  if (info["author"]) cleanInfo["author"] = String(info["author"]);
  if (info["subject"]) cleanInfo["subject"] = String(info["subject"]);
  if (info["keywords"]) cleanInfo["keywords"] = String(info["keywords"]);
  if (info["creator"]) cleanInfo["creator"] = String(info["creator"]);
  if (info["producer"]) cleanInfo["producer"] = String(info["producer"]);

  // Se creationDate existir, DEVE ser um Date nativo válido
  if (info["creationDate"] !== undefined && info["creationDate"] !== null) {
    if (!(info["creationDate"] instanceof Date) || Number.isNaN(info["creationDate"].getTime()) || !Number.isFinite(info["creationDate"].getTime())) {
      throw new AppError(ErrorCode.Validation, `Tipo inválido para data de criação do documento (creationDate não é Date válido).`);
    }
    cleanInfo["creationDate"] = info["creationDate"];
  }

  // Se modDate existir, DEVE ser um Date nativo válido
  if (info["modDate"] !== undefined && info["modDate"] !== null) {
    if (!(info["modDate"] instanceof Date) || Number.isNaN(info["modDate"].getTime()) || !Number.isFinite(info["modDate"].getTime())) {
      throw new AppError(ErrorCode.Validation, `Tipo inválido para data de modificação do documento (modDate não é Date válido).`);
    }
    cleanInfo["modDate"] = info["modDate"];
  }

  docDefinition.info = cleanInfo;
}

export function normalizePdfCreationDate(value: unknown): Date {
  if (value === null || value === undefined) {
    throw new AppError(ErrorCode.Validation, "Data de criação do documento ausente.");
  }
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      throw new AppError(ErrorCode.Validation, "Data de criação do documento inválida (NaN).");
    }
    return value;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      throw new AppError(ErrorCode.Validation, "Data de criação do documento não pode ser vazia.");
    }
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
      throw new AppError(ErrorCode.Validation, `Data de criação do documento em formato inválido: "${value}"`);
    }
    return parsed;
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value) || Number.isNaN(value)) {
      throw new AppError(ErrorCode.Validation, "Timestamp de criação do documento inválido.");
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      throw new AppError(ErrorCode.Validation, "Timestamp de criação do documento inválido.");
    }
    return parsed;
  }
  throw new AppError(ErrorCode.Validation, "Tipo inválido para data de criação do documento.");
}

export function triggerBrowserDownload(bytes: Uint8Array, filename: string): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  const blob = new Blob([bytes as BlobPart], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".pdf") ? filename : `${filename}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

function base64ToUint8Array(base64: string): Uint8Array {
  if (typeof Buffer !== "undefined") {
    const buf = Buffer.from(base64, "base64");
    return new Uint8Array(buf.buffer.slice(buf.byteOffset, buf.byteOffset + buf.byteLength));
  }
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function uint8ArrayToBase64(bytes: Uint8Array): string {
  if (typeof Buffer !== "undefined") {
    return Buffer.from(bytes.buffer, bytes.byteOffset, bytes.byteLength).toString("base64");
  }
  let binary = "";
  const len = bytes.byteLength;
  const chunkSize = 0x8000;
  for (let i = 0; i < len; i += chunkSize) {
    const chunk = bytes.subarray(i, Math.min(i + chunkSize, len));
    binary += String.fromCharCode.apply(null, chunk as unknown as number[]);
  }
  return btoa(binary);
}

async function extractPdfBuffer(generator: any): Promise<Uint8Array> {
  // 1. Tenta API baseada em Promise
  if (typeof generator.getBuffer === "function") {
    try {
      const res = generator.getBuffer();
      if (res && typeof res.then === "function") {
        const buf = await res;
        if (buf instanceof Uint8Array) return buf;
        if (typeof Buffer !== "undefined" && Buffer.isBuffer(buf)) {
          return new Uint8Array(buf.buffer.slice(buf.byteOffset, buf.byteOffset + buf.byteLength));
        }
        if (buf instanceof ArrayBuffer) return new Uint8Array(buf);
      }
    } catch {
      // fallback para callback
    }
  }

  if (typeof generator.getBase64 === "function") {
    try {
      const res = generator.getBase64();
      if (res && typeof res.then === "function") {
        const b64 = await res;
        if (typeof b64 === "string" && b64.length > 0) {
          return base64ToUint8Array(b64);
        }
      }
    } catch {
      // fallback para callback
    }
  }

  // 2. Fallback baseado em callback
  return new Promise((resolve, reject) => {
    let resolved = false;
    const doneBuf = (buf: any) => {
      if (resolved) return;
      resolved = true;
      if (buf instanceof Uint8Array) resolve(buf);
      else if (typeof Buffer !== "undefined" && Buffer.isBuffer(buf)) resolve(new Uint8Array(buf.buffer.slice(buf.byteOffset, buf.byteOffset + buf.byteLength)));
      else if (buf instanceof ArrayBuffer) resolve(new Uint8Array(buf));
      else if (typeof buf === "string") resolve(base64ToUint8Array(buf));
      else reject(new AppError(ErrorCode.Internal, "Formato binário de PDF não reconhecido retornado pelo gerador"));
    };
    const doneB64 = (b64: string) => {
      if (resolved) return;
      resolved = true;
      resolve(base64ToUint8Array(b64));
    };

    if (typeof generator.getBuffer === "function") {
      generator.getBuffer(doneBuf);
    } else if (typeof generator.getBase64 === "function") {
      generator.getBase64(doneB64);
    } else if (typeof generator.getDataUrl === "function") {
      generator.getDataUrl((dataUrl: string) => {
        const clean = dataUrl.startsWith("data:") ? dataUrl.split(",")[1] || "" : dataUrl;
        doneB64(clean);
      });
    } else {
      reject(new AppError(ErrorCode.Internal, "Nenhum método de extração de PDF disponível"));
    }
  });
}
function pdfInfoMetadata(): { author: string; creator: string; keywords: string } {
  const version = typeof __APP_VERSION__ !== "undefined" && __APP_VERSION__ ? __APP_VERSION__ : null;
  const buildNumber = typeof __APP_BUILD_TIME__ !== "undefined" && __APP_BUILD_TIME__ ? __APP_BUILD_TIME__ : null;
  const environment = typeof __APP_BACKEND_ENV__ !== "undefined" && __APP_BACKEND_ENV__ ? __APP_BACKEND_ENV__ : null;
  const appVersion = resolveAppVersion({ version, buildNumber, environment });
  return {
    author: appVersion.productName,
    creator: appVersion.productName,
    keywords: `ApplicationVersion=${appVersion.displayVersion}`
  };
}

export interface DocumentSnapshot {
  id: string;
  type: string;
  revision: string;
  data: any;
  createdAt: Date;
  documentNumber?: string;
  /**
   * Client the document belongs to. Documents whose client is chosen inside
   * the document itself (e.g. the ME report) carry it here, so generation
   * does not depend on the session's globally selected client.
   */
  clientId?: string;
}

export interface DocumentBuilder {
  build(snapshot: DocumentSnapshot): TDocumentDefinitions;
}

export class DocumentEngine {
  private builders = new Map<string, DocumentBuilder>();

  /**
   * Um tipo de documento tem UM builder. Registrar outro por cima era um
   * Map.set silencioso — foi assim que um placeholder passou a responder pelo
   * Relatório LP e o PDF real saiu vazio. Agora o conflito é um erro.
   */
  registerBuilder(type: string, builder: DocumentBuilder) {
    const existing = this.builders.get(type);
    if (existing && existing !== builder) {
      throw new AppError(ErrorCode.Internal, `Document builder for type "${type}" is already registered`, {
        safeMessage: `Já existe um builder registrado para o documento "${type}". Registro duplicado recusado.`
      });
    }
    this.builders.set(type, builder);
  }

  private async getPdfMake(): Promise<any> {
    try {
      if (typeof process !== "undefined" && process.versions && process.versions.node && (typeof window === "undefined" || (window as any).__IS_NODE_TEST__)) {
        const { createRequire } = await import("node:module");
        const req = createRequire(import.meta.url);
        const lib = req("pdfmake");
        const vfsModule = req("pdfmake/build/vfs_fonts.js");
        const vfs = vfsModule.pdfMake ? vfsModule.pdfMake.vfs : (vfsModule.vfs || vfsModule);
        if (vfs && lib.virtualfs) {
          for (const name of Object.keys(vfs)) {
            if (!lib.virtualfs.existsSync(name)) {
              lib.virtualfs.writeFileSync(name, Buffer.from(vfs[name], "base64"));
            }
          }
        }
        if (typeof lib.setFonts === "function") {
          lib.setFonts({
            Roboto: {
              normal: "Roboto-Regular.ttf",
              bold: "Roboto-Medium.ttf",
              italics: "Roboto-Italic.ttf",
              bolditalics: "Roboto-MediumItalic.ttf"
            }
          });
        }
        if (typeof lib.setUrlAccessPolicy === "function") lib.setUrlAccessPolicy(() => false);
        if (typeof lib.setLocalAccessPolicy === "function") lib.setLocalAccessPolicy(() => false);
        return lib;
      }
    } catch {
      // Fallback to client browser import
    }

    const pdfMakeModule = await import("pdfmake/build/pdfmake.js");
    const pdfFontsModule = await import("pdfmake/build/vfs_fonts.js");

    const pdfMake = (pdfMakeModule as any).default || pdfMakeModule;
    const pdfFonts = (pdfFontsModule as any).default || pdfFontsModule;

    const vfs = pdfFonts['pdfMake'] ? pdfFonts['pdfMake']['vfs'] : (pdfFonts['vfs'] || pdfFonts);
    if (vfs) {
      (pdfMake as any).vfs = vfs;
    }
    return pdfMake;
  }

  /** Tipos com builder registrado (depois de importar builders/index). */
  registeredTypes(): string[] {
    return [...this.builders.keys()].sort();
  }

  /**
   * Diagnóstico (CONFIGURAÇÕES → DIAGNÓSTICO → TESTAR PDF): gera de verdade um
   * PDF mínimo com o mesmo pdfmake dos relatórios. Não usa builder nem dado de
   * documento, não grava nada e não baixa arquivo.
   */
  async selfTest(): Promise<{ ok: boolean; message: string }> {
    try {
      const bytes = await this.generatePdfBytes({
        content: [{ text: "INSPECT IA — teste do motor de PDF" }],
        info: {
          title: "SELF_TEST",
          creationDate: new Date()
        }
      });
      const types = this.registeredTypes();
      return bytes.length > 200
        ? { ok: true, message: `PDF de teste gerado (${bytes.length} bytes). Builders registrados: ${types.length > 0 ? types.join(", ") : "nenhum carregado ainda"}.` }
        : { ok: false, message: "O motor devolveu um PDF vazio." };
    } catch (e: any) {
      return { ok: false, message: `Falha ao gerar o PDF de teste: ${e?.message || "motivo não informado"}.` };
    }
  }

  /**
   * Prepara incondicionalmente a definição do documento e normaliza a metadata
   * (creationDate e modDate) imediatamente antes de chamar pdfMake.createPdf.
   * Remove quaisquer propriedades com maiúsculas (CreationDate/ModDate) que possam
   * interferir no gerador do pdfkit.
   */
  private prepareDocumentDefinitionForPdf(
    snapshotOrDocDef: DocumentSnapshot | TDocumentDefinitions
  ): { docDefinition: TDocumentDefinitions; snapshotForAudit: DocumentSnapshot | null } {
    let docDefinition: TDocumentDefinitions;
    let snapshotForAudit: DocumentSnapshot | null = null;

    if ("type" in snapshotOrDocDef && "revision" in snapshotOrDocDef && typeof (snapshotOrDocDef as any).type === "string") {
      const snapshot = snapshotOrDocDef as DocumentSnapshot;
      snapshotForAudit = snapshot;
      this.validateAccess(snapshot);
      const builder = this.builders.get(snapshot.type);
      if (!builder) throw new AppError(ErrorCode.Validation, "Builder not found", { safeMessage: `No builder registered for document type: ${snapshot.type}` });
      docDefinition = builder.build(snapshot);

      const rawDate =
        snapshot.createdAt ??
        (snapshot.data && (snapshot.data.printDate || snapshot.data.createdAt || snapshot.data.date || snapshot.data.header?.emissionDate || snapshot.data.emissionDate || snapshot.data.issueDate)) ??
        (docDefinition.info as any)?.creationDate ??
        (docDefinition.info as any)?.CreationDate;

      if (!rawDate) {
        throw new AppError(ErrorCode.Validation, "Data de criação do documento ausente no snapshot.");
      }
      const stableDate = normalizePdfCreationDate(rawDate);

      const rawInfo = { ...(docDefinition.info || {}) };
      delete (rawInfo as any).CreationDate;
      delete (rawInfo as any).ModDate;

      let stableModDate: Date | undefined;
      if (rawInfo.modDate !== undefined && rawInfo.modDate !== null) {
        try {
          stableModDate = normalizePdfCreationDate(rawInfo.modDate);
        } catch {
          stableModDate = undefined;
        }
      }

      docDefinition.info = {
        ...rawInfo,
        title: `${snapshot.type}_${snapshot.id}_${snapshot.revision}`,
        subject: "Relatório Técnico",
        creationDate: stableDate,
        ...(stableModDate ? { modDate: stableModDate } : {}),
        ...pdfInfoMetadata()
      };

      (docDefinition.info as any).creationDate = stableDate;
      delete (docDefinition.info as any).CreationDate;
      delete (docDefinition.info as any).ModDate;
      if (!(docDefinition.info as any).modDate) {
        delete (docDefinition.info as any).modDate;
      }

      assertDrawableImages(docDefinition, snapshot);
    } else {
      docDefinition = snapshotOrDocDef as TDocumentDefinitions;
      if (docDefinition.info) {
        const rawInfo = { ...(docDefinition.info || {}) };
        delete (rawInfo as any).CreationDate;
        delete (rawInfo as any).ModDate;

        let stableCreationDate: Date | undefined;
        if (rawInfo.creationDate !== undefined && rawInfo.creationDate !== null) {
          stableCreationDate = normalizePdfCreationDate(rawInfo.creationDate);
        }

        let stableModDate: Date | undefined;
        if (rawInfo.modDate !== undefined && rawInfo.modDate !== null) {
          try {
            stableModDate = normalizePdfCreationDate(rawInfo.modDate);
          } catch {
            stableModDate = undefined;
          }
        }

        docDefinition.info = {
          ...rawInfo,
          ...(stableCreationDate ? { creationDate: stableCreationDate } : {}),
          ...(stableModDate ? { modDate: stableModDate } : {})
        };
        if (stableCreationDate) (docDefinition.info as any).creationDate = stableCreationDate;
        delete (docDefinition.info as any).CreationDate;
        delete (docDefinition.info as any).ModDate;
        if (!(docDefinition.info as any).modDate) {
          delete (docDefinition.info as any).modDate;
        }
      }
    }

    assertPdfMetadataReady(docDefinition);

    console.log("[PDF-METADATA-FINAL]", {
      module: snapshotForAudit?.type || "DOCDEF",
      infoKeys: Object.keys(docDefinition.info || {}),
      CreationDateExists: "CreationDate" in (docDefinition.info || {}),
      creationDateType: typeof (docDefinition.info as any)?.creationDate,
      creationDateIsDate: (docDefinition.info as any)?.creationDate instanceof Date,
      creationDateTimestampValid: (docDefinition.info as any)?.creationDate instanceof Date && Number.isFinite(((docDefinition.info as any)?.creationDate as Date).getTime())
    });

    return { docDefinition, snapshotForAudit };
  }

  /**
   * Núcleo ÚNICO de compilação PDF: prepara a definição, invoca pdfMake e
   * devolve os bytes binários brutos (Uint8Array) validados com assinatura %PDF-.
   */
  async compilePdfBytes(snapshotOrDocDef: DocumentSnapshot | TDocumentDefinitions): Promise<Uint8Array> {
    console.log("[PDF-RUNTIME]", {
      module: snapshotOrDocDef && (snapshotOrDocDef as any).type ? (snapshotOrDocDef as any).type : "DOCDEF",
      documentType: snapshotOrDocDef && (snapshotOrDocDef as any).type ? (snapshotOrDocDef as any).type : "TDocumentDefinitions",
      snapshotId: snapshotOrDocDef && (snapshotOrDocDef as any).id ? (snapshotOrDocDef as any).id : null,
      engineInstanceId: DOCUMENT_ENGINE_RUNTIME_ID,
      entryPoint: "compilePdfBytes",
      runtimeCommit: getRuntimeBuildInfo().commit || getRuntimeBuildInfo().buildTimestamp
    });

    const { docDefinition, snapshotForAudit } = this.prepareDocumentDefinitionForPdf(snapshotOrDocDef);

    let bytes: Uint8Array;
    try {
      const pdfMake = await this.getPdfMake();
      const pdfDocGenerator = pdfMake.createPdf(docDefinition);
      bytes = await extractPdfBuffer(pdfDocGenerator);
    } catch (err: any) {
      console.error("[PDF-CREATE-ERROR]", {
        module: snapshotForAudit?.type || "DOCDEF",
        name: err?.name,
        message: err?.message,
        stack: err?.stack
      });
      throw err;
    }

    if (!bytes || bytes.length === 0) {
      throw new AppError(ErrorCode.Internal, "O motor de PDF devolveu um buffer vazio.");
    }

    // Validação estrita da assinatura binária %PDF-
    if (bytes.length < 5 || bytes[0] !== 0x25 || bytes[1] !== 0x50 || bytes[2] !== 0x44 || bytes[3] !== 0x46 || bytes[4] !== 0x2D) {
      throw new AppError(ErrorCode.Internal, "Assinatura do arquivo PDF inválida (ausência de %PDF-).");
    }

    console.log("[PDF-BYTES]", {
      module: snapshotForAudit?.type || "DOCDEF",
      byteLength: bytes.length,
      first5: `${bytes[0]},${bytes[1]},${bytes[2]},${bytes[3]},${bytes[4]}`
    });

    if (snapshotForAudit) {
      this.auditLog("DOCUMENT_GENERATED", snapshotForAudit);
    }
    return bytes;
  }

  /**
   * Gera os bytes binários (Uint8Array) do PDF a partir de um DocumentSnapshot
   * ou TDocumentDefinitions, executando tanto em Node.js quanto em Navegador.
   */
  async generatePdfBytes(snapshotOrDocDef: DocumentSnapshot | TDocumentDefinitions): Promise<Uint8Array> {
    return this.compilePdfBytes(snapshotOrDocDef);
  }

  async generatePdf(snapshot: DocumentSnapshot): Promise<string> {
    const bytes = await this.compilePdfBytes(snapshot);
    const base64 = uint8ArrayToBase64(bytes);
    return `data:application/pdf;base64,${base64}`;
  }

  async downloadPdf(snapshot: DocumentSnapshot, filename: string): Promise<Uint8Array> {
    const bytes = await this.compilePdfBytes(snapshot);
    const sanitizedFilename = filename.replace(/[^a-zA-Z0-9_.-]/g, "_");
    this.auditLog("DOCUMENT_DOWNLOADED", snapshot);

    if (typeof window !== "undefined" && typeof document !== "undefined" && typeof document.createElement === "function") {
      triggerBrowserDownload(bytes, sanitizedFilename);
    }
    return bytes;
  }

  private validateAccess(snapshot: DocumentSnapshot) {
    const ctx = contextStore.get();
    // A document must identify its client — either the one carried by the
    // snapshot (documents that select their own client, like the ME report)
    // or the session's active client, or found within the snapshot payload.
    const owningClient =
      snapshot.clientId ||
      ctx.activeClientId ||
      snapshot.data?.clientId ||
      snapshot.data?.report?.clientId ||
      snapshot.data?.rdo?.clientId ||
      snapshot.data?.client?.id ||
      snapshot.data?.model?.header?.clientId ||
      snapshot.data?.clientName ||
      snapshot.data?.client;
    if (!owningClient && typeof window !== "undefined") {
      throw new AppError(ErrorCode.Authorization, "No client identified for document generation", {
        safeMessage: "Não foi possível identificar o cliente do documento. Selecione o cliente do relatório e tente novamente."
      });
    }
    // Basic RBAC/tenant validation would go here for real implementation
  }

  private auditLog(action: string, snapshot: DocumentSnapshot) {
    const ctx = contextStore.get();
    console.log(`[AUDIT] [${new Date().toISOString()}] User=${ctx.userName} Client=${ctx.activeClientId} Action=${action} Doc=${snapshot.type} ID=${snapshot.id} Rev=${snapshot.revision}`);
  }
}

export const documentEngine = new DocumentEngine();

/**
 * Percorre a definição e recusa antes de o pdfmake tentar desenhar.
 *
 * O motor só desenha PNG e JPEG. Quando recebe outra coisa, a falha acontece
 * dentro do trabalho assíncrono dele — a Promise não rejeita, e o erro chega
 * ao usuário como `Unhandled Promise Rejection: Unknown image format`, sem
 * dizer de qual documento nem de qual campo. Conferir aqui é o que permite
 * dar uma mensagem que diz o que fazer.
 *
 * A caminhada é feita uma vez, sobre a definição já montada, e ignora o
 * conteúdo: só olha nós `image:`.
 */
function assertDrawableImages(definition: unknown, snapshot: DocumentSnapshot): void {
  const rejected: Array<{ path: string; reason: string; detail: string }> = [];

  const walk = (node: unknown, path: string, depth: number): void => {
    if (!node || typeof node !== "object" || depth > 24) return;
    if (Array.isArray(node)) {
      node.forEach((child, i) => walk(child, `${path}[${i}]`, depth + 1));
      return;
    }
    const record = node as Record<string, unknown>;
    if ("image" in record) {
      const resolved = resolvePdfImage(record['image']);
      if (resolved.kind === "invalid") {
        rejected.push({ path: `${path}.image`, reason: resolved.reason, detail: resolved.detail });
      } else if (resolved.kind === "raster") {
        // Normaliza de passagem: base64 legado vira dataURL antes do motor.
        record['image'] = resolved.dataUrl;
      } else if (resolved.kind === "svg") {
        rejected.push({
          path: `${path}.image`,
          reason: "Uma imagem vetorial (SVG) foi colocada num campo de imagem comum.",
          detail: "svg em image:, deveria ser svg:"
        });
      }
    }
    for (const [key, value] of Object.entries(record)) {
      if (key === "image") continue;
      walk(value, `${path}.${key}`, depth + 1);
    }
  };

  walk(definition, "doc", 0);
  if (rejected.length === 0) return;

  // Metadados no console; o conteúdo das imagens nunca.
  for (const item of rejected) {
    console.error("[PDF] PDF_IMAGE_VALIDATION_FAILED", {
      documentType: snapshot.type,
      documentId: snapshot.documentNumber || snapshot.id,
      field: item.path,
      reason: item.reason,
      detail: item.detail
    });
  }

  const primeiro = rejected[0]!;
  throw new AppError(ErrorCode.Validation, `Undrawable image in ${snapshot.type} (${rejected.length})`, {
    safeMessage: rejected.length === 1
      ? `NÃO É POSSÍVEL GERAR O PDF. ${primeiro.reason}`
      : `NÃO É POSSÍVEL GERAR O PDF. ${rejected.length} imagens do documento estão em formato inválido. A primeira: ${primeiro.reason}`
  });
}
